import {
  API_HOST_XOR_ENV_PLATFORM_MESSAGE,
  hasApiHostXorEnvPlatform,
  withApiHostOrEnvPlatform,
} from '~/vendor/cx-sdk/http-core';
import { z } from 'zod';

import { type Repub, REPUBS } from '~/vendor/cx-modules/constants';

import { type SubscriptionInterval, type SubscriptionTerm } from './types';

/**
 * Subscription interval constants defining the billing frequency.
 */
export const SUBSCRIPTION_INTERVAL = {
  MONTH: 'MONTH',
  YEAR: 'YEAR',
} as const;

/**
 * Subscription term constants for human-readable billing frequencies.
 */
export const SUBSCRIPTION_TERM = {
  ANNUAL: 'annually',
  MONTHLY: 'monthly',
} as const;

/**
 * The feature schema.
 */
export const featureSchema = z.object({
  name: z.string(),
});

/**
 * The type of product schema.
 */
export const productTypeSchema = z.enum(['RECURRING', 'ONE_OFF']);

/**
 * The provider schema.
 */
export const providerSchema = z.enum(['STRIPE', 'APPLE', 'GOOGLE']);

/**
 * The subscription interval schema.
 */
export const subscriptionIntervalSchema = z.enum([SUBSCRIPTION_INTERVAL.MONTH, SUBSCRIPTION_INTERVAL.YEAR]);

/**
 * The subscription term schema.


/**
 * Schema for validating subscription terms.
 */
export const subscriptionTermSchema = z.enum([SUBSCRIPTION_TERM.ANNUAL, SUBSCRIPTION_TERM.MONTHLY]);

/**
 * Schema for validating Stripe price IDs.
 */
export const priceIdRegex = z.string().regex(/^price_.*$/);

/**
 * Schema for validating price objects.
 */
export const priceSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  id: priceIdRegex,
  metadata: z.object({
    repub: z.enum(Object.keys(REPUBS) as [Repub, ...Repub[]]),
  }),
  recurring: z.object({
    count: z.number(),
    interval: z.enum(
      Object.values(SUBSCRIPTION_INTERVAL) as [
        (typeof SUBSCRIPTION_INTERVAL)[keyof typeof SUBSCRIPTION_INTERVAL],
        ...Array<(typeof SUBSCRIPTION_INTERVAL)[keyof typeof SUBSCRIPTION_INTERVAL]>,
      ],
    ),
  }),
  type: productTypeSchema,
});

/**
 * Schema for validating Stripe product IDs.
 */
const productIdRegex = z.string().regex(/^prod_.*$/);

/**
 * Schema for validating basic product objects.
 */
export const productSchema = z.object({
  defaultPrice: z.string().optional(),
  description: z.string(),
  features: z.array(z.object({ name: z.string() })),
  id: productIdRegex,
  images: z.array(z.string()),
  metadata: z.record(z.string(), z.unknown()),
  name: z.string(),
  prices: z.array(priceSchema),
});

/**
 * Schema for validating OC Premium products.
 */
export const ocPremiumProductSchema = productSchema.extend({
  name: z.string().includes('Premium'),
});

/**
 * Schema for validating OC Essential products.
 */
export const ocEssentialProductSchema = productSchema.extend({
  name: z.string().includes('Essential'),
});

/**
 * Schema for validating checkout options.
 */
export const checkoutOptionsSchema = z.object({
  affiliateId: z.string().optional(),
  authToken: z.string().optional(),
  couponCode: z.string().optional(),
  customerLocale: z.string().optional(),
  deviceId: z.string().optional(),
  priceId: z.string(),
  provider: providerSchema.optional(),
  quantity: z.number().optional(),
  trialDays: z.number().optional(),
  type: productTypeSchema,
  urls: z.object({ cancelUrl: z.string(), successUrl: z.string() }),
});

/**
 * Schema for validating get subscriptions options.
 */
export const getSubscriptionsOptionsSchema = z.object({
  authToken: z.string().optional(),
  deviceId: z.string().optional(),
});

/**
 * Schema for validating portal session options.
 */
export const portalSessionOptionsSchema = z.object({
  authToken: z.string().optional(),
  customerLocale: z.string().optional(),
  deviceId: z.string().optional(),
  returnUrl: z.string(),
});

/**
 * Schema for validating user subscription objects.
 */
export const userSubscriptionSchema = z.object({
  price: priceSchema,
  product: productSchema,
  receipt: z.object({
    id: z.string(),
    timestamp: z.string(),
  }),
  startDate: z.string(),
  endDate: z.string(),
  type: z.string(),
  status: z.string(),
  provider: z.string(),
});

/**
 * Schema for validating user newsletter objects.
 */
export const userNewsletterSchema = z.object({
  name: z.string(),
});

/**
 * Schema for validating base SDK constructor parameters.
 */
export const sdkConstructorBaseParamsSchema = z.object({
  apiKey: z.string(),
  authToken: z.string().optional(),
  deviceId: z.string(),
  maxRetryAttempts: z.number().optional(),
});

/**
 * Schema for validating SDK constructor parameters.
 * Requires either apiHost OR both environment and platform, but not both combinations.
 */
export const sdkConstructorParamsSchema = withApiHostOrEnvPlatform(sdkConstructorBaseParamsSchema).refine(
  hasApiHostXorEnvPlatform,
  {
    message: API_HOST_XOR_ENV_PLATFORM_MESSAGE,
  },
);

/**
 * Converts a subscription interval to its corresponding subscription term.
 * @param interval - The subscription interval to convert
 * @returns The corresponding subscription term
 * @throws {Error} If an invalid subscription interval is provided
 */
export function getSubscriptionTerm(interval: SubscriptionInterval): SubscriptionTerm {
  switch (interval) {
    case SUBSCRIPTION_INTERVAL.MONTH:
      return SUBSCRIPTION_TERM.MONTHLY;
    case SUBSCRIPTION_INTERVAL.YEAR:
      return SUBSCRIPTION_TERM.ANNUAL;
    default:
      throw new Error('Invalid subscription term');
  }
}

/**
 * Type guard to check if a value is a valid subscription interval.
 * @param interval - The value to check
 * @returns True if the value is a valid subscription interval, false otherwise
 */
export function isValidSubscriptionInterval(interval: unknown): interval is z.infer<typeof subscriptionIntervalSchema> {
  return subscriptionIntervalSchema.safeParse(interval).success;
}

/**
 * Type guard to check if a value is a valid subscription term.
 * @param term - The value to check
 * @returns True if the value is a valid subscription term, false otherwise
 */
export function isValidSubscriptionTerm(term: unknown): term is z.infer<typeof subscriptionTermSchema> {
  return subscriptionTermSchema.safeParse(term).success;
}

/**
 * Type guard to check if a value is a valid price object.
 * @param price - The value to check
 * @returns True if the value is a valid price object, false otherwise
 */
export function isValidPrice(price: unknown): price is z.infer<typeof priceSchema> {
  return priceSchema.safeParse(price).success;
}

/**
 * Type guard to check if a value is a valid product.
 * @param product - The value to check
 * @returns True if the value is a valid product, false otherwise
 */
export function isValidProduct(product: unknown): product is z.infer<typeof productSchema> {
  return productSchema.safeParse(product).success;
}

/**
 * Type guard to check if a value is a valid OC Premium product.
 * @param product - The value to check
 * @returns True if the value is a valid OC Premium product, false otherwise
 */
export function isValidOCPremiumProduct(product: unknown): product is z.infer<typeof ocPremiumProductSchema> {
  return ocPremiumProductSchema.safeParse(product).success;
}

/**
 * Type guard to check if a value is a valid OC Essential product.
 * @param product - The value to check
 * @returns True if the value is a valid OC Essential product, false otherwise
 */
export function isValidOCEssentialProduct(product: unknown): product is z.infer<typeof ocEssentialProductSchema> {
  return ocEssentialProductSchema.safeParse(product).success;
}
